export const PERIOD_TYPES = {
  COURSE_PERIODS: "course_periods",
  DAY_COURSE_PERIODS: "day_course_periods",
  SCHEDULE_PERIODS: "schedule_periods",
};

const ALTERNATING_SCHEDULE_TYPES = new Set(["ab", "ab_skinny"]);

const getSortedDayKeys = (schedule_structure = {}) => {
  return Object.keys(schedule_structure)
    .filter((day_key) => `${day_key}`.trim() !== "")
    .sort((day_a, day_b) => parseInt(day_a, 10) - parseInt(day_b, 10));
};

const getValidDayKey = (schedule_structure = {}, current_day = null) => {
  const sorted_day_keys = getSortedDayKeys(schedule_structure);
  if (sorted_day_keys.length === 0) return null;

  const requested_day_key = `${current_day ?? ""}`;
  if (requested_day_key && requested_day_key in schedule_structure) return requested_day_key;

  return sorted_day_keys[0];
};

const getScheduledCoursePeriodIdSet = (schedule_structure = {}) => {
  const scheduled_course_period_ids = new Set();

  for (const day_key of Object.keys(schedule_structure)) {
    const day_schedule_periods = schedule_structure[day_key] || [];
    for (const day_schedule_period_info of day_schedule_periods) {
      const course_period_id = day_schedule_period_info?.course_period_id;
      if (course_period_id === null || course_period_id === undefined) continue;

      const normalized_course_period_id = `${course_period_id}`.trim();
      if (!normalized_course_period_id) continue;

      scheduled_course_period_ids.add(normalized_course_period_id);
    }
  }

  return scheduled_course_period_ids;
};

const getDayScheduleStructure = (schedule_structure_data, current_day = null) => {
  const schedule_structure = schedule_structure_data?.schedule_structure || {};
  const day_key = getValidDayKey(schedule_structure, current_day);
  if (!day_key) return [];
  return schedule_structure[day_key] || [];
};

export const getPeriodType = (schedule_type, current_view_type) => {
  if (!schedule_type || !current_view_type) return null;

  if (schedule_type === "daily" || current_view_type === "day") {
    return PERIOD_TYPES.SCHEDULE_PERIODS;
  }

  if (ALTERNATING_SCHEDULE_TYPES.has(schedule_type)) {
    return PERIOD_TYPES.DAY_COURSE_PERIODS;
  }

  return PERIOD_TYPES.COURSE_PERIODS;
};

export const getPeriodTypeFromScheduleType = (
  schedule_type,
  { alternating_uses_day_course_periods = true } = {}
) => {
  if (!schedule_type) return null;

  if (schedule_type === "daily") return PERIOD_TYPES.SCHEDULE_PERIODS;

  if (alternating_uses_day_course_periods && ALTERNATING_SCHEDULE_TYPES.has(schedule_type)) {
    return PERIOD_TYPES.DAY_COURSE_PERIODS;
  }

  return PERIOD_TYPES.COURSE_PERIODS;
};

export const parseCoursePeriodSortKey = (period_name) => {
  const normalized_period_name = `${period_name ?? ""}`.trim();
  const numeric_prefix_match = normalized_period_name.match(/^(\d+)\s*([A-Za-z]*)(.*)$/);

  if (!numeric_prefix_match) {
    return {
      bucket: 1,
      numeric_prefix: Number.MAX_SAFE_INTEGER,
      alpha_suffix: "",
      remainder: normalized_period_name.toLowerCase(),
    };
  }

  const numeric_prefix = parseInt(numeric_prefix_match[1], 10);
  return {
    bucket: 0,
    numeric_prefix: Number.isNaN(numeric_prefix) ? Number.MAX_SAFE_INTEGER : numeric_prefix,
    alpha_suffix: (numeric_prefix_match[2] || "").toUpperCase(),
    remainder: (numeric_prefix_match[3] || "").trim().toLowerCase(),
  };
};

export const sortCoursePeriodsNaturally = (course_periods = []) => {
  const sorted_periods = [...course_periods].sort((period_a, period_b) => {
    const period_a_name = `${period_a?.period_name ?? ""}`;
    const period_b_name = `${period_b?.period_name ?? ""}`;
    const sort_key_a = parseCoursePeriodSortKey(period_a_name);
    const sort_key_b = parseCoursePeriodSortKey(period_b_name);

    if (sort_key_a.bucket !== sort_key_b.bucket) {
      return sort_key_a.bucket - sort_key_b.bucket;
    }

    if (sort_key_a.numeric_prefix !== sort_key_b.numeric_prefix) {
      return sort_key_a.numeric_prefix - sort_key_b.numeric_prefix;
    }

    const alpha_suffix_comparison = sort_key_a.alpha_suffix.localeCompare(sort_key_b.alpha_suffix, undefined, {
      sensitivity: "base",
      numeric: true,
    });
    if (alpha_suffix_comparison !== 0) return alpha_suffix_comparison;

    const remainder_comparison = sort_key_a.remainder.localeCompare(sort_key_b.remainder, undefined, {
      sensitivity: "base",
      numeric: true,
    });
    if (remainder_comparison !== 0) return remainder_comparison;

    return period_a_name.localeCompare(period_b_name, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  });

  return sorted_periods.map((period_info, index) => ({
    ...period_info,
    period_order: index + 1,
  }));
};

export const buildPeriodsToShow = (
  schedule_structure_data,
  {
    period_type,
    current_day = null,
    natural_sort_course_periods = true,
  } = {}
) => {
  if (!schedule_structure_data || !period_type) return [];

  const schedule_structure = schedule_structure_data.schedule_structure || {};
  const schedule_periods = schedule_structure_data.schedule_periods || {};
  const course_periods = schedule_structure_data.course_periods || {};
  const scheduled_course_period_ids = getScheduledCoursePeriodIdSet(schedule_structure);

  if (period_type === PERIOD_TYPES.COURSE_PERIODS) {
    const unsorted_periods = Object.keys(course_periods).reduce((results, course_period_id) => {
      const period_info = course_periods[course_period_id];
      if (!period_info) return results;

      const normalized_course_period_id = `${course_period_id}`;
      const normalized_period_id_from_info = `${period_info.period_id || course_period_id}`;
      const period_is_in_schedule =
        scheduled_course_period_ids.has(normalized_course_period_id) ||
        scheduled_course_period_ids.has(normalized_period_id_from_info);
      if (!period_is_in_schedule) return results;

      results.push({
        ...period_info,
        period_id: period_info.period_id || `${course_period_id}`,
      });
      return results;
    }, []);

    if (natural_sort_course_periods) {
      return sortCoursePeriodsNaturally(unsorted_periods);
    }

    return unsorted_periods
      .map((period_info) => ({
        ...period_info,
        period_order: parseInt(period_info.period_name, 10),
      }))
      .sort((period_a, period_b) => {
        if (!period_a.period_order) return 1;
        if (!period_b.period_order) return -1;
        return period_a.period_order - period_b.period_order;
      });
  }

  if (period_type === PERIOD_TYPES.DAY_COURSE_PERIODS) {
    let period_order = 1;
    const day_course_periods = [];
    const day_keys = getSortedDayKeys(schedule_structure);

    for (const day_key of day_keys) {
      const day_schedule_periods = schedule_structure[day_key] || [];
      for (const day_schedule_period_info of day_schedule_periods) {
        const course_period_id = day_schedule_period_info?.course_period_id;
        if (!course_period_id) continue;

        const period_info = course_periods[course_period_id];
        if (!period_info) continue;

        day_course_periods.push({
          ...period_info,
          period_id: period_info.period_id || `${course_period_id}`,
          period_order,
        });
        period_order++;
      }
    }

    return day_course_periods;
  }

  if (period_type === PERIOD_TYPES.SCHEDULE_PERIODS) {
    const day_key = getValidDayKey(schedule_structure, current_day);
    if (!day_key) return [];

    const day_schedule_periods = schedule_structure[day_key] || [];
    return day_schedule_periods.reduce((results, period_info) => {
      const schedule_period_id = period_info?.schedule_period_id;
      if (!schedule_period_id) return results;

      const schedule_period_info = schedule_periods[schedule_period_id];
      if (!schedule_period_info) return results;

      results.push({
        ...period_info,
        ...schedule_period_info,
        period_id: schedule_period_info.period_id || `${schedule_period_id}`,
      });
      return results;
    }, []);
  }

  return [];
};

export const getCoursePeriodIdFromSchedulePeriodId = (
  schedule_structure_data,
  schedule_period_id,
  current_day = null
) => {
  if (!schedule_structure_data || schedule_period_id === null || schedule_period_id === undefined) {
    return null;
  }

  const normalized_schedule_period_id = `${schedule_period_id}`;
  const day_schedule_structure = getDayScheduleStructure(schedule_structure_data, current_day);
  const day_schedule_row = day_schedule_structure.find(
    (schedule_row) => `${schedule_row?.schedule_period_id}` === normalized_schedule_period_id
  );
  if (day_schedule_row?.course_period_id) return day_schedule_row.course_period_id;

  const schedule_structure = schedule_structure_data.schedule_structure || {};
  for (const day_key of Object.keys(schedule_structure)) {
    const fallback_row = (schedule_structure[day_key] || []).find(
      (schedule_row) => `${schedule_row?.schedule_period_id}` === normalized_schedule_period_id
    );
    if (fallback_row?.course_period_id) return fallback_row.course_period_id;
  }

  return null;
};

export const getSchedulePeriodIdFromCoursePeriodId = (
  schedule_structure_data,
  course_period_id,
  current_day = null
) => {
  if (!schedule_structure_data || course_period_id === null || course_period_id === undefined) {
    return null;
  }

  const course_periods = schedule_structure_data.course_periods || {};
  const normalized_course_period_id = `${course_period_id}`;
  const course_period_info = course_periods[normalized_course_period_id] || course_periods[course_period_id];
  const days = course_period_info?.days || {};
  const day_key = `${current_day ?? ""}`;

  if (day_key && day_key in days && days[day_key]) return days[day_key];
  if ("1" in days && days["1"]) return days["1"];

  const fallback_day_keys = Object.keys(days);
  if (fallback_day_keys.length > 0) {
    const fallback_day_key = fallback_day_keys.sort(
      (day_a, day_b) => parseInt(day_a, 10) - parseInt(day_b, 10)
    )[0];
    if (fallback_day_key && days[fallback_day_key]) return days[fallback_day_key];
  }

  return null;
};

export const getCoursePeriodIdForPeriod = (
  schedule_structure_data,
  period_id,
  {
    period_type = null,
    schedule_type = null,
    current_day = null,
  } = {}
) => {
  if (period_id === null || period_id === undefined) return null;

  const period_requires_schedule_to_course_mapping =
    period_type === PERIOD_TYPES.SCHEDULE_PERIODS || schedule_type === "daily";

  if (!period_requires_schedule_to_course_mapping) return period_id;

  return getCoursePeriodIdFromSchedulePeriodId(schedule_structure_data, period_id, current_day);
};

export const getPeriodNameFromCoursePeriodId = (
  schedule_structure_data,
  course_period_id,
  {
    schedule_type = null,
    current_day = null,
  } = {}
) => {
  if (!schedule_structure_data || course_period_id === null || course_period_id === undefined) {
    return null;
  }

  const normalized_course_period_id = `${course_period_id}`;
  const course_periods = schedule_structure_data.course_periods || {};
  const schedule_periods = schedule_structure_data.schedule_periods || {};
  const course_period_info =
    course_periods[normalized_course_period_id] || course_periods[course_period_id];

  if (!course_period_info) return null;

  if (schedule_type !== "daily") {
    return course_period_info.period_name || null;
  }

  const schedule_period_id = getSchedulePeriodIdFromCoursePeriodId(
    schedule_structure_data,
    normalized_course_period_id,
    current_day
  );
  if (!schedule_period_id) return null;

  const normalized_schedule_period_id = `${schedule_period_id}`;
  const schedule_period_info =
    schedule_periods[normalized_schedule_period_id] || schedule_periods[schedule_period_id];

  return schedule_period_info?.period_name || null;
};
